import * as RD from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn';

/*
  Dialog и Sheet — всплывающие слои поверх содержимого (DESIGN.md R5: страницы не трогают Radix
  и порталы напрямую). Подложка — токен overlay, слой z-modal, радиус 20 (R8), выезд — рецептами
  motion.css. Sheet — та же модалка, прижатая к правому краю: уведомления, свойства объекта.

  Модалка нужна только там, где требуется ввод (имя, причина, слово-подтверждение). Необратимое
  действие без ввода — удержанием (R27, HoldToConfirm), а не вопросом «вы уверены?».
*/

export const Dialog = RD.Root;
export const DialogTrigger = RD.Trigger;
export const DialogClose = RD.Close;

const dialogContent = cva(
  [
    'fixed left-1/2 z-modal flex w-full -translate-x-1/2 flex-col rounded-xl border border-line bg-elevated shadow-xl',
    'focus-visible:outline-none data-[state=closed]:animate-pop-out data-[state=open]:animate-pop-in',
  ],
  {
    variants: {
      size: {
        sm: 'max-w-sm',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
      },
      placement: {
        center: 'top-1/2 -translate-y-1/2',
        /** Командная строка: ближе к верху, чтобы список рос вниз, не прыгая. */
        top: 'top-24',
      },
    },
    defaultVariants: { size: 'md', placement: 'center' },
  },
);

function Overlay() {
  return (
    <RD.Overlay className="fixed inset-0 z-overlay bg-overlay data-[state=closed]:animate-backdrop-out data-[state=open]:animate-backdrop-in" />
  );
}

interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof RD.Content>, VariantProps<typeof dialogContent> {
  /** Крестик в углу. В командной строке не нужен: её закрывает Esc. */
  closeButton?: boolean;
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  ({ className, size, placement, closeButton = true, children, ...rest }, ref) => (
    <RD.Portal>
      <Overlay />
      <RD.Content
        ref={ref}
        data-slot="dialog"
        className={cn(dialogContent({ size, placement }), className)}
        {...rest}
      >
        {children}
        {closeButton && (
          <RD.Close
            aria-label="Закрыть"
            className="absolute top-4 right-4 grid size-8 place-items-center rounded-md text-fg-subtle transition-colors duration-fast hover:bg-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <X className="size-4" />
          </RD.Close>
        )}
      </RD.Content>
    </RD.Portal>
  ),
);
DialogContent.displayName = 'DialogContent';

export const SheetContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RD.Content>>(
  ({ className, children, ...rest }, ref) => (
    <RD.Portal>
      <Overlay />
      <RD.Content
        ref={ref}
        data-slot="sheet"
        className={cn(
          'fixed inset-y-3 right-3 z-modal flex w-96 max-w-full flex-col overflow-hidden rounded-xl border border-line bg-elevated shadow-xl',
          'focus-visible:outline-none data-[state=closed]:animate-slide-out-right data-[state=open]:animate-slide-in-right',
          className,
        )}
        {...rest}
      >
        {children}
      </RD.Content>
    </RD.Portal>
  ),
);
SheetContent.displayName = 'SheetContent';

export function DialogHeader({
  title,
  description,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 px-6 pt-5 pb-4">
      <RD.Title className="pr-10 type-heading text-fg">{title}</RD.Title>
      {description ? (
        <RD.Description className="type-caption text-fg-muted">{description}</RD.Description>
      ) : (
        <RD.Description className="sr-only">{typeof title === 'string' ? title : 'Окно'}</RD.Description>
      )}
      {children}
    </div>
  );
}

/** Скрытый заголовок для окон без видимого заголовка (командная строка): читалкам он нужен. */
export function DialogHiddenTitle({ children }: { children: string }) {
  return (
    <>
      <RD.Title className="sr-only">{children}</RD.Title>
      <RD.Description className="sr-only">{children}</RD.Description>
    </>
  );
}
